import {
  type ColumnFiltersState,
  createColumnHelper,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { Copy, InfoIcon, Search } from 'lucide-react';
import * as React from 'react';
import { useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';

import { useGetAllUsers } from '~entities/users/api/get-all-users';

import { useGetAuditEvents } from '~features/audit-logs/api/get-audit-events';
import {
  type AuditLog,
  useGetAuditLogs,
} from '~features/audit-logs/api/get-audit-logs';
import { useGetAuditResources } from '~features/audit-logs/api/get-audit-resources';

import { DatePickerWithRange } from '~shared/components/date-picker-with-range';
import {
  EnrollioTable,
  EnrollioTableColumnToggle,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import { useStudioDateFormatter } from '~shared/hooks/useStudioDateFormatter';
import { Badge } from '~shared/shadcn/ui/badge';
import { Card, CardContent } from '~shared/shadcn/ui/card';
import { Input } from '~shared/shadcn/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import { copyToClipboard } from '~shared/utils/clipboard';

import {
  formatAuditValue,
  getAuditEventBadgeVariant,
  resolveAuditLogActor,
  resolveAuditLogTitle,
} from '../audit-logs.helpers';
import { AuditLogDetailsModal } from './audit-log-details-modal';

const AUDIT_LOG_SORT_FIELD_MAP: Record<
  string,
  'createdAt' | 'updatedAt' | 'eventType' | 'resourceType'
> = {
  createdAt: 'createdAt',
  eventType: 'eventType',
  resourceType: 'resourceType',
};

const columnHelper = createColumnHelper<AuditLog>();

export function AuditLogsTable() {
  const { formatInStudioTz } = useStudioDateFormatter();
  const [resourceFilter, setResourceFilter] = useState<string>('All');
  const [eventFilter, setEventFilter] = useState<string>('All');
  const [actorFilter, setActorFilter] = useState<string>('All');
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [resourceIdSearch, setResourceIdSearch] = useState<string>('');
  const [resourceNameSearch, setResourceNameSearch] = useState<string>('');
  const [selectedAuditLog, setSelectedAuditLog] = useState<AuditLog | null>(
    null,
  );
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: getStoredPageSize(20),
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const { data: resourcesData } = useGetAuditResources();
  const { data: eventsData } = useGetAuditEvents();
  const { data: usersData } = useGetAllUsers();

  const availableResources = resourcesData ?? [];
  const availableEvents = eventsData ?? [];
  const availableUsers = usersData ?? [];

  const activeSort = sorting[0];
  const sortByParam =
    activeSort && AUDIT_LOG_SORT_FIELD_MAP[activeSort.id]
      ? AUDIT_LOG_SORT_FIELD_MAP[activeSort.id]
      : 'createdAt';
  const sortOrderParam: 'asc' | 'desc' = activeSort
    ? activeSort.desc
      ? 'desc'
      : 'asc'
    : 'desc';

  const auditLogsParams = {
    offset: pagination.pageIndex * pagination.pageSize,
    limit: pagination.pageSize,
    resourceType: resourceFilter !== 'All' ? resourceFilter : undefined,
    eventType: eventFilter !== 'All' ? eventFilter : undefined,
    actorId: actorFilter !== 'All' ? actorFilter : undefined,
    resourceId: resourceIdSearch.trim() || undefined,
    resourceName: resourceNameSearch.trim() || undefined,
    startDate: dateRange?.from
      ? format(dateRange.from, 'yyyy-MM-dd')
      : undefined,
    endDate: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
    sortBy: sortByParam,
    sortOrder: sortOrderParam,
  };

  const {
    data: auditLogsData,
    isFetching,
    error,
  } = useGetAuditLogs(auditLogsParams);

  const data = auditLogsData?.data ?? [];

  const totalCount = auditLogsData?.pagination.total ?? 0;
  const handleSortingChange = React.useCallback(
    (updater: React.SetStateAction<SortingState>) => {
      setSorting((prev) => {
        const nextSortedState =
          typeof updater === 'function' ? updater(prev) : updater;
        setPagination((prevPagination) => ({
          ...prevPagination,
          pageIndex: 0,
        }));
        return nextSortedState;
      });
    },
    [],
  );

  const cols = useMemo(() => {
    return [
      columnHelper.accessor('resourceName', {
        id: 'resourceName',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Name'
          />
        ),
        cell: (info) => {
          const row = info.row.original;
          const { resourceId, resourceType, studentDisplayName } = row;
          const isEnrollment = resourceType?.toLowerCase() === 'enrollment';
          const classTitle = resolveAuditLogTitle(row);

          const handleCopyResourceId = async (e: React.MouseEvent) => {
            e.stopPropagation();
            if (resourceId) {
              const success = await copyToClipboard(resourceId);
              if (!success) {
                console.error('Failed to copy resource ID');
              }
            }
          };

          return (
            <div className='px-4'>
              <div className='text-sm font-medium'>{classTitle}</div>
              {isEnrollment && studentDisplayName ? (
                <div className='text-xs text-muted-foreground'>
                  {studentDisplayName}
                </div>
              ) : null}
              {resourceId ? (
                <div className='mt-0.5 flex items-center gap-1'>
                  <span className='font-mono text-xs text-muted-foreground'>
                    ID {resourceId}
                  </span>
                  <button
                    type='button'
                    onClick={handleCopyResourceId}
                    className='inline-flex items-center justify-center rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
                    aria-label='Copy resource ID'
                  >
                    <Copy className='size-3' />
                  </button>
                </div>
              ) : null}
            </div>
          );
        },
      }),
      columnHelper.accessor('actorEmail', {
        id: 'actorEmail',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Done By'
          />
        ),
        cell: (info) => {
          const row = info.row.original;
          const { displayName, subEmail } = resolveAuditLogActor(row);

          return (
            <div className='px-4'>
              <div className='text-sm font-medium'>{displayName}</div>
              <div className='text-xs text-muted-foreground'>
                {subEmail}
                {row.actorType ? ` • ${row.actorType}` : ''}
              </div>
            </div>
          );
        },
      }),
      columnHelper.accessor('resourceType', {
        id: 'resourceType',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Resource'
          />
        ),
        cell: (info) => (
          <div className='px-4'>
            <Badge variant='outline'>
              {formatAuditValue(info.getValue() ?? '')}
            </Badge>
          </div>
        ),
      }),
      columnHelper.accessor('eventType', {
        id: 'eventType',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Event'
          />
        ),
        cell: (info) => {
          const eventType = info.getValue() ?? '';

          return (
            <div className='px-4'>
              <Badge variant={getAuditEventBadgeVariant(eventType)}>
                {formatAuditValue(eventType)}
              </Badge>
            </div>
          );
        },
      }),
      columnHelper.accessor('createdAt', {
        id: 'createdAt',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Date & Time'
          />
        ),
        cell: (info) => {
          try {
            const formattedDate = formatInStudioTz(
              info.getValue(),
              "MMM dd, yyyy 'at' h:mm aa",
            );
            return <div className='px-4'>{formattedDate}</div>;
          } catch {
            return <div className='px-4'>{info.getValue()}</div>;
          }
        },
      }),
      columnHelper.accessor('changes', {
        id: 'details',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title=''
            disableSorting={true}
          />
        ),
        cell: (info) => {
          const auditLog = info.row.original;

          return (
            <div className='px-4'>
              <button
                type='button'
                onClick={() => {
                  setSelectedAuditLog(auditLog);
                  setIsDetailsModalOpen(true);
                }}
                className='cursor-pointer text-muted-foreground transition-colors hover:text-foreground'
                aria-label='View audit log details'
              >
                <InfoIcon className='size-4' />
              </button>
            </div>
          );
        },
      }),
    ];
  }, [formatInStudioTz]);

  return (
    <>
      <Card>
        <CardContent className='pt-6'>
          {error && (
            <div className='mb-6 rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive'>
              Error loading audit logs:{' '}
              {error instanceof Error ? error.message : 'Unknown error'}
            </div>
          )}
          {!error && !isFetching && data.length === 0 && (
            <div className='mb-6 rounded-md border p-4 text-center text-sm text-muted-foreground'>
              No audit logs found
            </div>
          )}
          <EnrollioTable
            ariaLabel='Audit logs'
            totalLabel='Audit logs'
            filterHeader={
              <>
                <div className='relative w-[200px]'>
                  <Search className='absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' />
                  <Input
                    placeholder='Search by Name...'
                    type='search'
                    className='w-full pl-8'
                    value={resourceNameSearch}
                    onChange={(e) => {
                      setResourceNameSearch(e.target.value);
                      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    }}
                  />
                </div>
                <div className='relative w-full md:w-[200px]'>
                  <Search className='absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' />
                  <Input
                    placeholder='Search by ID...'
                    type='search'
                    className='w-full pl-8'
                    value={resourceIdSearch}
                    onChange={(e) => {
                      setResourceIdSearch(e.target.value);
                      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    }}
                  />
                </div>
                {/* Date Range Filter */}
                <DatePickerWithRange
                  date={dateRange}
                  setDate={(range) => {
                    setDateRange(range);
                    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                  }}
                />
                {/* Filter Actor/User */}
                <Select
                  value={actorFilter}
                  onValueChange={(value) => {
                    setActorFilter(value);
                    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                  }}
                >
                  <SelectTrigger className='w-[200px]'>
                    <SelectValue placeholder='Select User' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='All'>All Users</SelectItem>
                    {availableUsers.map((user) => (
                      <SelectItem key={user.id} value={user.id ?? ''}>
                        {user.firstName} {user.lastName} ({user.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* Filter Resource */}
                <Select
                  value={resourceFilter}
                  onValueChange={(value) => {
                    setResourceFilter(value);
                    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                  }}
                >
                  <SelectTrigger className='w-[180px]'>
                    <SelectValue placeholder='Resource Type' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='All'>All Resources</SelectItem>
                    {availableResources.map((resource) => (
                      <SelectItem key={resource.value} value={resource.value}>
                        {resource.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* Filter Event */}
                <Select
                  value={eventFilter}
                  onValueChange={(value) => {
                    setEventFilter(value);
                    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                  }}
                >
                  <SelectTrigger className='w-[180px]'>
                    <SelectValue placeholder='Event Type' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='All'>All Events</SelectItem>
                    {availableEvents.map((event) => (
                      <SelectItem key={event.value} value={event.value}>
                        {event.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            }
            toolbar={(table) => <EnrollioTableColumnToggle table={table} />}
            columns={cols}
            data={data}
            isUpdating={isFetching}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            columnFilters={columnFilters}
            onColumnFiltersChange={setColumnFilters}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            pagination={pagination}
            onPaginationChange={setPagination}
            manualPagination
            manualSorting
            rowCount={totalCount}
          />
        </CardContent>
      </Card>

      <AuditLogDetailsModal
        open={isDetailsModalOpen}
        onOpenChange={setIsDetailsModalOpen}
        auditLog={selectedAuditLog}
      />
    </>
  );
}
